/**
 * Merchant Time Slots
 *
 * The slot shown here is CORE's (`merchant_settings.pickup_time_slot_code`). Core
 * still owns it, but an officer can now change it from here: the write goes to
 * core's own slot service, which validates the code against the live grid, busts
 * core's settings cache, and re-slots the merchant's open pickups. That last part
 * is why the confirmation reports how many pickups moved — a default change is not
 * a settings tweak, it can move live routed work.
 *
 * The other thing an officer edits here is the dedicated-PA binding, which is
 * fleet-owned (Mongo `merchant_pickup_windows`) and materially changes routing:
 * a dedicated merchant's work is held out of auto-generation AND out of the
 * append sweep, for the officer to place by hand. See
 * `tasks/dedicated-driver-explained.md`.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent, Badge, Button } from '../components/ui';
import {
  Search,
  RefreshCw,
  Store,
  Loader2,
  Clock,
  Pencil,
  X,
  Check,
  AlertCircle,
  AlertTriangle,
  Truck,
  LayoutList,
  BarChart3,
  Lock,
} from 'lucide-react';
import * as merchantPickupWindowsApi from '../services/merchantPickupWindows.api';
import { getMerchantSlots, setMerchantSlot, SLOT_ORDER, slotStyle } from '../services/merchantSlots.api';
import type { MerchantSlot, ReslottedReport } from '../services/merchantSlots.api';
import TimelineView from '../components/merchant-timeline/TimelineView';
import { getDrivers } from '../services/drivers.api';
import { matchesSearch } from '../utils/search';
import { useCan } from '../contexts/useAuth';

/**
 * Edit the dedicated-PA binding for one merchant. There is no time-window editor
 * any more — the merchant's slot lives in Core and cannot be written from here.
 */
interface DedicatedModalProps {
  merchant: MerchantSlot;
  onClose: () => void;
  onSave: (data: {
    requiresDedicatedDriver: boolean;
    dedicatedDriverUid: string | null;
    dedicatedDriverName: string | null;
    notes?: string;
  }) => void;
  isSaving: boolean;
  backendError?: string | null;
}

function DedicatedModal({ merchant, onClose, onSave, isSaving, backendError }: DedicatedModalProps) {
  const [requiresDedicatedDriver, setRequiresDedicatedDriver] = useState(
    merchant.requires_dedicated_driver
  );
  const [dedicatedDriverUid, setDedicatedDriverUid] = useState(merchant.dedicated_driver_uid || '');
  const [notes, setNotes] = useState(merchant.notes || '');

  // Only fetched once the flag is on — the picker is the only consumer.
  const { data: drivers = [] } = useQuery({
    queryKey: ['drivers'],
    queryFn: getDrivers,
    enabled: requiresDedicatedDriver,
    staleTime: 5 * 60_000,
  });

  const style = slotStyle(merchant.slot_code);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      requiresDedicatedDriver,
      // Clearing the flag clears the binding; a shared-pool merchant naming a
      // PA nobody looks at is worse than no binding at all.
      dedicatedDriverUid: requiresDedicatedDriver ? dedicatedDriverUid || null : null,
      dedicatedDriverName: requiresDedicatedDriver
        ? drivers.find((d) => d.driver_uid === dedicatedDriverUid)?.full_name ?? null
        : null,
      notes: notes || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Dedicated PA</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {backendError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {backendError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Merchant</label>
            <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Store className="h-4 w-4 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">{merchant.merchant_name}</div>
                <div className="text-xs text-gray-500 truncate">
                  ID: {merchant.merchant_id} • {merchant.merchant_uid}
                </div>
              </div>
            </div>
          </div>

          {/* Read-only in THIS modal: the slot has its own dialog, because
              changing it moves live pickups and needs a reason. */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Pickup Slot <span className="font-normal text-gray-500">(set in Core)</span>
            </label>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <Lock className="h-4 w-4 flex-shrink-0 text-gray-400" />
              {merchant.slot_code ? (
                <span className={`rounded border px-2 py-0.5 text-sm font-medium ${style.badge}`}>
                  {merchant.slot_code}
                  {merchant.slot_window ? ` · ${merchant.slot_window.from}–${merchant.slot_window.to}` : ''}
                </span>
              ) : (
                <span className="text-sm text-gray-500">No slot set</span>
              )}
              <span className="ml-auto text-xs text-gray-500">Use the clock button to change it</span>
            </div>
          </div>

          <div className="p-3 border border-orange-200 rounded-lg bg-orange-50">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresDedicatedDriver}
                onChange={(e) => setRequiresDedicatedDriver(e.target.checked)}
                className="w-5 h-5 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
              />
              <div>
                <span className="font-medium text-gray-900">Requires Dedicated Driver</span>
                <p className="mt-0.5 text-xs text-gray-500">
                  This merchant's pickups and returns are held out of auto-generated routes.
                  The officer places them on the PA's route.
                </p>
              </div>
            </label>

            {requiresDedicatedDriver && (
              <div className="mt-3 border-t border-orange-200 pt-3">
                <label className="mb-1 block text-sm font-medium text-gray-700">Dedicated PA</label>
                <select
                  value={dedicatedDriverUid}
                  onChange={(e) => setDedicatedDriverUid(e.target.value)}
                  className="w-full rounded-lg border border-orange-300 px-3 py-2 text-sm"
                >
                  <option value="">Not chosen yet</option>
                  {drivers.map((d) => (
                    <option key={d.driver_uid} value={d.driver_uid}>
                      {d.full_name}
                    </option>
                  ))}
                </select>
                {!dedicatedDriverUid && (
                  // Held-back work with nobody named is the failure mode worth
                  // warning about — it just piles up unassigned.
                  <p className="mt-1 text-xs text-orange-700">
                    Without a PA the work is still held back, but nobody is named to collect it.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Any additional notes about this merchant..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export interface SlotOption {
  code: string;
  label: string | null;
  window: { from: string; to: string } | null;
  /** false only when core says so; unknown slots stay selectable. */
  active: boolean | null;
}

/**
 * Change a merchant's DEFAULT pickup slot.
 *
 * `reason` is required rather than nice-to-have: core persists it on the audit row
 * for every pickup this change moves, so it is the only record of why a merchant's
 * standing arrangement changed.
 */
interface SlotModalProps {
  merchant: MerchantSlot;
  slots: SlotOption[];
  onClose: () => void;
  onSave: (data: { timeSlotCode: string; reason: string }) => void;
  isSaving: boolean;
  backendError?: string | null;
}

function SlotModal({ merchant, slots, onClose, onSave, isSaving, backendError }: SlotModalProps) {
  const [timeSlotCode, setTimeSlotCode] = useState(merchant.slot_code || '');
  const [reason, setReason] = useState('');

  const trimmedReason = reason.trim();
  const unchanged = timeSlotCode === merchant.slot_code;
  const canSave = !!timeSlotCode && trimmedReason.length > 0 && !unchanged && !isSaving;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    onSave({ timeSlotCode, reason: trimmedReason });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Change pickup slot</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {backendError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {backendError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Merchant</label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-gray-900 truncate">{merchant.merchant_name}</div>
              <div className="text-xs text-gray-500 truncate">
                ID: {merchant.merchant_id} • {merchant.merchant_uid}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Current slot: {merchant.slot_code ? `${merchant.slot_code}` : 'none set'}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="slot-code" className="mb-1 block text-sm font-medium text-gray-700">
              New slot
            </label>
            <select
              id="slot-code"
              value={timeSlotCode}
              onChange={(e) => setTimeSlotCode(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Select a slot…</option>
              {slots.map((s) => (
                <option key={s.code} value={s.code} disabled={s.active === false}>
                  {s.code}
                  {s.window ? ` · ${s.window.from}–${s.window.to}` : ''}
                  {s.label ? ` · ${s.label}` : ''}
                  {s.active === false ? ' (inactive)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="slot-reason" className="mb-1 block text-sm font-medium text-gray-700">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="slot-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={255}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="e.g. Merchant called support to move their daily pickup later"
            />
            <p className="mt-1 text-xs text-gray-500">
              Stored on the audit row for every pickup this change moves. Max 255 characters.
            </p>
          </div>

          {/* The consequence, before they commit to it — not after. */}
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              This also moves the merchant's already-open pickups from today onward onto the new slot.
              Pickups already on a route move too and Fleet re-plans them. Pickups awaiting a rollover
              response, and collected or cancelled ones, are left alone.
            </span>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {unchanged && timeSlotCode ? 'Pick a different slot' : 'Change slot'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MerchantPickupWindows() {
  const can = useCan();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [slotFilter, setSlotFilter] = useState<string | null>(null);
  const [dedicatedOnly, setDedicatedOnly] = useState(false);
  const [includeUnset, setIncludeUnset] = useState(false);
  const [editing, setEditing] = useState<MerchantSlot | null>(null);
  const [editingSlot, setEditingSlot] = useState<MerchantSlot | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table');
  const [backendError, setBackendError] = useState<string | null>(null);
  const [slotError, setSlotError] = useState<string | null>(null);
  const [slotNotice, setSlotNotice] = useState<
    { merchant: string; code: string; reslotted: ReslottedReport | null } | null
  >(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['merchantSlots', includeUnset],
    queryFn: () => getMerchantSlots(includeUnset),
  });

  const merchants = data?.rows;
  const meta = data?.meta;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['merchantSlots'] });

  /**
   * The dedicated binding lives on the fleet doc, which may not exist yet for a
   * merchant that only ever had a Core slot — so create-or-update, keeping
   * `timeWindows: []` because free-form windows are retired.
   */
  const saveMutation = useMutation({
    mutationFn: async (input: {
      merchant: MerchantSlot;
      requiresDedicatedDriver: boolean;
      dedicatedDriverUid: string | null;
      dedicatedDriverName: string | null;
      notes?: string;
    }) => {
      const { merchant, ...binding } = input;
      if (merchant.fleet_doc_id) {
        return merchantPickupWindowsApi.updateMerchantPickupWindow(merchant.fleet_doc_id, {
          ...binding,
          // A previously-deactivated doc is invisible to routing; saving a
          // binding onto one has to reactivate it or the change does nothing.
          isActive: true,
        });
      }
      return merchantPickupWindowsApi.createMerchantPickupWindow({
        merchantId: merchant.merchant_id,
        merchantUid: merchant.merchant_uid ?? '',
        merchantName: merchant.merchant_name,
        timeWindows: [],
        ...binding,
      });
    },
    onSuccess: () => {
      invalidate();
      setEditing(null);
      setBackendError(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      setBackendError(err?.response?.data?.error || 'Failed to save dedicated PA');
    },
  });

  /**
   * Core's default slot write. Success is NOT reported as a bare "saved": the
   * response says how many of the merchant's open pickups core just moved, and
   * `relocated` ones were already on a route — the officer has to see that.
   */
  const slotMutation = useMutation({
    mutationFn: async (input: { merchant: MerchantSlot; timeSlotCode: string; reason: string }) =>
      setMerchantSlot({
        merchant_uid: input.merchant.merchant_uid ?? '',
        time_slot_code: input.timeSlotCode,
        reason: input.reason,
      }),
    onSuccess: (result, input) => {
      invalidate();
      setSlotNotice({
        merchant: input.merchant.merchant_name,
        code: input.timeSlotCode,
        reslotted: result.reslotted,
      });
      setEditingSlot(null);
      setSlotError(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      setSlotError(err?.response?.data?.error || 'Failed to change the pickup slot');
    },
  });

  /**
   * The five platform slots, with window/label/active taken from whichever loaded
   * row already carries them — the page has live slot config in its rows, so the
   * picker needs no extra request. A slot nobody sits in still has to be offerable,
   * which is exactly the case for a merchant being moved off "no slot set".
   */
  const slotOptions = useMemo<SlotOption[]>(() => {
    const seen = new Map<string, MerchantSlot>();
    for (const m of merchants ?? []) {
      if (m.slot_code && !seen.has(m.slot_code)) seen.set(m.slot_code, m);
    }
    return SLOT_ORDER.map((code) => {
      const row = seen.get(code);
      return {
        code,
        label: row?.slot_label ?? null,
        window: row?.slot_window ?? null,
        active: row?.slot_active ?? null,
      };
    });
  }, [merchants]);

  const filtered = useMemo(() => {
    if (!merchants) return [];
    const query = searchQuery.trim().toLowerCase();
    return merchants.filter((m) => {
      if (slotFilter === 'none' ? !!m.slot_code : slotFilter && m.slot_code !== slotFilter) return false;
      if (dedicatedOnly && !m.requires_dedicated_driver) return false;
      if (!query) return true;
      // Normalised both sides — Core's merchant names carry doubled spaces that
      // the browser collapses on screen. See utils/search.ts.
      return matchesSearch(query, m.merchant_name, String(m.merchant_id), m.merchant_uid);
    });
  }, [merchants, searchQuery, slotFilter, dedicatedOnly]);

  const slotCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of merchants ?? []) {
      const key = m.slot_code ?? 'none';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [merchants]);

  const orphanedDedicated = meta?.dedicated_without_driver ?? 0;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Merchant Time Slots</h1>
          <p className="text-gray-500 mt-1">
            Pickup slots as Core has them. Changing one re-slots the merchant's open pickups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/*
        The knock-on effect of a slot change, reported rather than swallowed: core
        moves the merchant's open pickups, and `relocated` ones were already on a
        route. Sticks around until dismissed — the officer may need to go look at
        those routes.
      */}
      {slotNotice && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
          <div className="flex-1 text-emerald-900">
            <span className="font-medium">
              {slotNotice.merchant} is now on {slotNotice.code}.
            </span>{' '}
            {slotNotice.reslotted ? (
              slotNotice.reslotted.moved === 0 ? (
                <span>No open pickups needed moving.</span>
              ) : (
                <span>
                  {slotNotice.reslotted.moved} open pickup
                  {slotNotice.reslotted.moved === 1 ? '' : 's'} moved onto the new slot
                  {slotNotice.reslotted.relocated > 0 && (
                    <>
                      {' '}— <span className="font-medium">{slotNotice.reslotted.relocated} already on a route</span>,
                      so Fleet is re-planning {slotNotice.reslotted.relocated === 1 ? 'it' : 'them'}
                    </>
                  )}
                  {slotNotice.reslotted.skipped > 0 && <> · {slotNotice.reslotted.skipped} skipped</>}.
                </span>
              )
            ) : (
              <span>Core did not report how many pickups moved.</span>
            )}
          </div>
          <button
            onClick={() => setSlotNotice(null)}
            className="text-emerald-600 hover:text-emerald-800"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Held back with nobody named — work that quietly piles up unassigned. */}
      {orphanedDedicated > 0 && (
        <button
          onClick={() => {
            setDedicatedOnly(true);
            setSlotFilter(null);
            setSearchQuery('');
          }}
          className="mb-4 flex w-full items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-left transition-colors hover:bg-red-100"
        >
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
          <div className="text-sm">
            <span className="font-medium text-red-800">
              {orphanedDedicated} merchant{orphanedDedicated === 1 ? '' : 's'} held out of auto-routing with no PA assigned.
            </span>
            <span className="ml-1 text-red-700">
              Their pickups are excluded from generated routes but nobody is named to collect them. Click to review.
            </span>
          </div>
        </button>
      )}

      {/* Search and Filters */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by merchant name, ID, or UID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={dedicatedOnly}
            onChange={(e) => setDedicatedOnly(e.target.checked)}
            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
          />
          <span className="text-sm text-gray-600">Dedicated only</span>
        </label>
        <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={includeUnset}
            onChange={(e) => setIncludeUnset(e.target.checked)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <span className="text-sm text-gray-600">Include merchants with no slot</span>
        </label>
      </div>

      {/* Slot chips */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setSlotFilter(null)}
          className={`rounded-full border px-3 py-1 text-sm transition-colors ${
            slotFilter === null ? 'border-purple-300 bg-purple-100 text-purple-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          All ({merchants?.length ?? 0})
        </button>
        {SLOT_ORDER.filter((c) => slotCounts.has(c)).map((code) => (
          <button
            key={code}
            onClick={() => setSlotFilter(slotFilter === code ? null : code)}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              slotFilter === code ? slotStyle(code).badge : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {code} ({slotCounts.get(code)})
          </button>
        ))}
        {slotCounts.has('none') && (
          <button
            onClick={() => setSlotFilter(slotFilter === 'none' ? null : 'none')}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              slotFilter === 'none' ? 'border-gray-300 bg-gray-200 text-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            No slot ({slotCounts.get('none')})
          </button>
        )}
      </div>

      {/* View mode toggle */}
      <div className="mb-4 flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setViewMode('table')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
            viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <LayoutList className="h-4 w-4" />
          Table
        </button>
        <button
          onClick={() => setViewMode('timeline')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
            viewMode === 'timeline' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BarChart3 className="h-4 w-4" />
          Timeline
        </button>
      </div>

      {viewMode === 'timeline' ? (
        <TimelineView merchants={filtered} />
      ) : (
        <Card padding="none">
          <CardHeader className="px-4 pt-4">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-600" />
              Merchant Time Slots ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                <span className="ml-2 text-gray-500">Loading merchant slots...</span>
              </div>
            ) : isError ? (
              <div className="text-center py-12 text-red-500">Failed to load merchant slots</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <Store className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No merchants match these filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Merchant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pickup Slot (Core)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Core Settings
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dedicated PA
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filtered.map((m) => {
                      const style = slotStyle(m.slot_code);
                      return (
                        <tr key={m.merchant_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Store className="h-4 w-4 text-purple-600" />
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900">{m.merchant_name}</div>
                                <div className="text-xs text-gray-500">
                                  ID: {m.merchant_id} • {m.merchant_uid}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {m.slot_code ? (
                              <div className="flex flex-col gap-0.5">
                                <span className={`w-fit rounded border px-2 py-0.5 text-sm font-medium ${style.badge}`}>
                                  {m.slot_code}
                                  {m.slot_window ? ` · ${m.slot_window.from}–${m.slot_window.to}` : ''}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {m.slot_label}
                                  {/* An inactive slot still holds merchants — worth seeing. */}
                                  {m.slot_active === false && (
                                    <span className="ml-1 text-amber-600">· slot inactive</span>
                                  )}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">No slot set</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {m.preferred_timing && (
                                <Badge variant="info" className="capitalize">{m.preferred_timing}</Badge>
                              )}
                              {m.auto_select_next_slot && <Badge variant="default">Auto next slot</Badge>}
                              {m.recurring_pickup_enabled && <Badge variant="default">Recurring</Badge>}
                              {m.bulk_pickup_enabled && <Badge variant="default">Bulk pickup</Badge>}
                              {m.bulk_rto_enabled && <Badge variant="default">Bulk RTO</Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {m.requires_dedicated_driver ? (
                              <div className="flex flex-col items-start gap-1">
                                <Badge
                                  variant="warning"
                                  className={
                                    m.dedicated_driver_name
                                      ? 'border-orange-200 bg-orange-100 text-orange-700'
                                      // Held out of routing with nobody named — the
                                      // state that quietly piles up unassigned work.
                                      : 'border-red-200 bg-red-100 text-red-700'
                                  }
                                  title={
                                    m.dedicated_driver_name
                                      ? `Served by ${m.dedicated_driver_name} — held out of auto-routing`
                                      : 'Held out of auto-routing, but no PA is assigned to collect it'
                                  }
                                >
                                  <Truck className="mr-1 h-3 w-3" />
                                  {m.dedicated_driver_name || 'Dedicated · no PA'}
                                </Badge>
                                {/* An inactive doc means routing ignores the binding entirely. */}
                                {m.fleet_doc_active === false && (
                                  <span className="text-xs text-amber-600">
                                    Inactive — routing treats this merchant as shared
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Shared pool</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                            {m.notes || '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {can('merchants.manage') && (
                              <button
                                onClick={() => {
                                  setSlotError(null);
                                  setEditingSlot(m);
                                }}
                                // No uid means no core identity to write against.
                                disabled={!m.merchant_uid}
                                className="p-2 text-gray-500 rounded hover:bg-purple-50 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                                title={
                                  m.merchant_uid
                                    ? 'Change pickup slot'
                                    : 'No core uid for this merchant — slot cannot be set from here'
                                }
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            )}
                            {can('merchants.manage') && (
                              <button
                                onClick={() => setEditing(m)}
                                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                                title="Edit dedicated PA"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {meta && (
        <p className="mt-3 text-xs text-gray-500">
          Slot source: <code>{meta.source}</code> · windows from live slot config
          {meta.slot_config?.fetched_at ? ` (fetched ${new Date(meta.slot_config.fetched_at).toLocaleString()})` : ''}
        </p>
      )}

      {editingSlot && (
        <SlotModal
          merchant={editingSlot}
          slots={slotOptions}
          onClose={() => {
            setEditingSlot(null);
            setSlotError(null);
          }}
          onSave={({ timeSlotCode, reason }) =>
            slotMutation.mutate({ merchant: editingSlot, timeSlotCode, reason })
          }
          isSaving={slotMutation.isPending}
          backendError={slotError}
        />
      )}

      {editing && (
        <DedicatedModal
          merchant={editing}
          onClose={() => {
            setEditing(null);
            setBackendError(null);
          }}
          onSave={(binding) => saveMutation.mutate({ merchant: editing, ...binding })}
          isSaving={saveMutation.isPending}
          backendError={backendError}
        />
      )}
    </div>
  );
}
